/**
 * Aushubparameter eines Arbeitsbereichs: lesen, zurueckschreiben, in die
 * Parameter des Schichtenschnitts uebersetzen.
 *
 * Reine Funktionen, kein DOM, kein jsPDF.
 *
 * ═══ WARUM ES DIESE DATEI GIBT ════════════════════════════════════════════
 * Der Schichtenschnitt wurde bis hierher mit fest verdrahteten Werten
 * gezeichnet — Aushubsohle „unbekannt", Ueberaushub „0,25 m", keine
 * Trennlage, keine Abdeckung. Das war fuer JEDES Feld dieselbe Aussage, und
 * sie stand in der Oberflaeche, nicht am Datensatz. Eine Zeichnung, die
 * Vorgaben statt Erfassungsstaende zeigt, sieht genauso aus wie eine
 * richtige.
 *
 * Die Werte gehoeren an den Arbeitsbereich (`asplan_cluster`), weil die
 * Aushubstrategie fuer alle Felder eines Bereichs gilt. Sie liegen als
 * benannter Unterschluessel in `umgebung` — die Begruendung steht bei
 * `AushubParameter` in `../types`.
 *
 * ═══ FEHLT IST NICHT NULL ═════════════════════════════════════════════════
 * `leseAushub` macht aus einem fehlenden Schluessel `null` und niemals 0
 * oder `false`. Der Unterschied ist der ganze Zweck: „0,00 m Abdeckung" sagt
 * aus, dass nicht abgedeckt wird; ein fehlender Wert sagt aus, dass es
 * niemand entschieden hat. Die Zeichnung stellt beides verschieden dar.
 */

import type { AushubParameter, Cluster } from '../types';
import type { SchnittParameter } from './schnitt';

/** Der Unterschluessel in `asplan_cluster.umgebung`. */
export const AUSHUB_SCHLUESSEL = 'aushub';

/** Nichts gesetzt — der Zustand eines frisch angelegten Bereichs. */
export const AUSHUB_LEER: AushubParameter = {
    sohle_m: null,
    ueberaushub_m: null,
    trennlage: null,
    abdeckung_m: null,
    schutzklasse_danach: '',
    weissbereich_danach: null,
};

const zahl = (w: unknown): number | null =>
    (typeof w === 'number' && Number.isFinite(w)) ? w : null;

const jaNein = (w: unknown): boolean | null =>
    (typeof w === 'boolean') ? w : null;

const wort = (w: unknown): string => (typeof w === 'string' ? w : '');

/**
 * Liest die Aushubparameter aus `umgebung`.
 *
 * Nimmt alles entgegen, was aus der Datenbank kommen kann — `undefined`,
 * `null`, ein Array, ein Objekt mit fremden Typen — und liefert immer einen
 * vollstaendigen Satz. Ein Wert falschen Typs gilt als nicht gesetzt und
 * nicht als 0: was nicht gelesen werden kann, darf nicht behauptet werden.
 */
export const leseAushub = (
    umgebung: Record<string, unknown> | null | undefined,
): AushubParameter => {
    const roh = umgebung ? umgebung[AUSHUB_SCHLUESSEL] : undefined;
    if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return { ...AUSHUB_LEER };

    const o = roh as Record<string, unknown>;
    return {
        sohle_m: zahl(o.sohle_m),
        ueberaushub_m: zahl(o.ueberaushub_m),
        trennlage: jaNein(o.trennlage),
        abdeckung_m: zahl(o.abdeckung_m),
        schutzklasse_danach: wort(o.schutzklasse_danach),
        weissbereich_danach: jaNein(o.weissbereich_danach),
    };
};

/**
 * Schreibt geaenderte Aushubparameter zurueck.
 *
 * Bewusst als Zusammenfuehrung und nicht als Ersetzung: in `umgebung` stehen
 * auch Temperatur, Belueftung, Enge und Witterung. Sie gehen in die
 * Tragezeitbegrenzung ein — sie hier zu ueberschreiben hiesse, eine
 * Schutzmassnahme beim Bearbeiten der Aushubsohle zu verlieren.
 */
export const mitAushub = (
    umgebung: Record<string, unknown> | null | undefined,
    u: Partial<AushubParameter>,
): Record<string, unknown> => ({
    ...(umgebung ?? {}),
    [AUSHUB_SCHLUESSEL]: { ...leseAushub(umgebung), ...u },
});

/** Ob fuer diesen Bereich ueberhaupt etwas zum Aushub erfasst ist. */
export const aushubErfasst = (a: AushubParameter): boolean =>
    a.sohle_m !== null || a.ueberaushub_m !== null || a.trennlage !== null
    || a.abdeckung_m !== null || a.weissbereich_danach !== null
    || a.schutzklasse_danach !== '';

/**
 * Baut die Parameter des Schichtenschnitts aus Feld und Arbeitsbereich.
 *
 * Die einzige Stelle, an der Bereich und Zeichnung zusammenkommen — damit
 * kein Aufrufer mehr eigene Vorgaben erfindet. Ist dem Feld kein Bereich
 * zugeordnet, bleibt alles offen; die Zeichnung sagt das dann selbst an.
 */
export const schnittParameterAusBereich = (
    feld_nr: string,
    bereich: Pick<Cluster, 'schutzklasse_key' | 'umgebung'> | undefined,
): SchnittParameter => {
    const a = leseAushub(bereich?.umgebung);
    return {
        feld_nr,
        aushubsohle_m: a.sohle_m,
        ueberaushub_m: a.ueberaushub_m,
        trennlage: a.trennlage,
        abdeckung_m: a.abdeckung_m,
        schutzklasse_aushub: bereich?.schutzklasse_key ?? '',
        schutzklasse_danach: a.schutzklasse_danach,
        weissbereich_danach: a.weissbereich_danach,
    };
};
